/**
 * Nomination Service Wrapper
 * Routes to GraphQL API (production) or in-memory mock (demo mode).
 * All methods are async to support the GraphQL path.
 */

import {
  Nomination,
  CreateNominationInput,
  UpdateNominationInput,
  ReviewerNote,
  ReviewerNoteInput,
  AuditEntry,
  FeedComment,
} from '../types/nomination';
import { nominationService, PaginatedFeed, NominationServiceResult } from './nominationService';
import { GraphQLNominationService } from './graphqlNomination';
import { isDemoMode } from './mockData';
import { getUrl } from 'aws-amplify/storage';

export type { PaginatedFeed, NominationServiceResult };

const shouldUseGraphQL = (): boolean => !isDemoMode();

// In-memory comment store (fallback until FeedComments table is deployed)
const localComments: Map<string, FeedComment[]> = new Map();

// Local photo URL cache (persisted in localStorage until nomineePhotoUrl is in the deployed schema)
const PHOTO_CACHE_KEY = 'nomination_photo_cache';

function getPhotoCache(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(PHOTO_CACHE_KEY) || '{}');
  } catch { return {}; }
}

function savePhotoUrl(nominationId: string, url: string): void {
  const cache = getPhotoCache();
  cache[nominationId] = url;
  localStorage.setItem(PHOTO_CACHE_KEY, JSON.stringify(cache));
}

// Resolve S3 keys to signed URLs. S3 keys look like "nominee-photos/xxx.jpg"
// Full URLs (http/data:) are passed through as-is.
const signedUrlCache: Map<string, { url: string; expires: number }> = new Map();

async function resolvePhotoUrl(photoUrl: string | undefined): Promise<string | undefined> {
  if (!photoUrl) return undefined;
  // Already a full URL (http, https, data:) — pass through
  if (photoUrl.startsWith('http') || photoUrl.startsWith('data:')) return photoUrl;
  // It's an S3 key — generate a signed URL
  const cached = signedUrlCache.get(photoUrl);
  if (cached && cached.expires > Date.now()) return cached.url;
  try {
    const startedAt = performance.now();
    console.log('[Perf][NominationPhotos] resolvePhotoUrl start', { photoUrl });
    const result = await getUrl({ key: photoUrl });
    const url = result.url.toString();
    // Cache for 50 minutes (signed URLs typically expire in 60 min)
    signedUrlCache.set(photoUrl, { url, expires: Date.now() + 50 * 60 * 1000 });
    console.log('[Perf][NominationPhotos] resolvePhotoUrl complete', {
      photoUrl,
      durationMs: Math.round(performance.now() - startedAt),
    });
    return url;
  } catch (err) {
    console.warn('[NominationWrapper] Failed to resolve photo URL:', photoUrl, err);
    return undefined;
  }
}

async function enrichWithResolvedPhotos(nominations: Nomination[]): Promise<Nomination[]> {
  const startedAt = performance.now();
  const cache = getPhotoCache();
  const nominationsWithPhotos = nominations.filter(n => n.nomineePhotoUrl || cache[n.id]).length;
  console.log('[Perf][NominationPhotos] enrichWithResolvedPhotos start', {
    nominationCount: nominations.length,
    nominationsWithPhotos,
  });
  const enriched = await Promise.all(
    nominations.map(async (n) => {
      const rawUrl = n.nomineePhotoUrl || cache[n.id] || undefined;
      const resolvedUrl = await resolvePhotoUrl(rawUrl);
      return { ...n, nomineePhotoUrl: resolvedUrl };
    })
  );
  console.log('[Perf][NominationPhotos] enrichWithResolvedPhotos complete', {
    durationMs: Math.round(performance.now() - startedAt),
    nominationCount: nominations.length,
    nominationsWithPhotos,
  });
  return enriched;
}

export class NominationServiceWrapper {

  static async createNomination(
    input: CreateNominationInput,
    nominatorId: string,
    nominatorName: string,
    asDraft: boolean = false
  ): Promise<NominationServiceResult> {
    let result: NominationServiceResult;
    if (shouldUseGraphQL()) {
      result = await GraphQLNominationService.createNomination(input, nominatorId, nominatorName, asDraft);
    } else {
      result = nominationService.createNomination(input, nominatorId, nominatorName, asDraft);
    }
    // Cache the photo URL locally since the deployed schema doesn't have nomineePhotoUrl yet
    if (result.success && result.nomination && input.nomineePhotoUrl) {
      savePhotoUrl(result.nomination.id, input.nomineePhotoUrl);
    }
    return result;
  }

  static async updateNomination(
    nominationId: string,
    updates: UpdateNominationInput,
    adminId: string,
    adminName: string
  ): Promise<NominationServiceResult> {
    if (shouldUseGraphQL()) {
      return GraphQLNominationService.updateNomination(nominationId, updates, adminId, adminName);
    }
    return nominationService.updateNomination(nominationId, updates, adminId, adminName);
  }

  static async deleteNomination(
    nominationId: string,
    adminId: string,
    adminName: string
  ): Promise<{ success: boolean; errors: string[] }> {
    if (shouldUseGraphQL()) {
      return GraphQLNominationService.deleteNomination(nominationId, adminId, adminName);
    }
    return nominationService.deleteNomination(nominationId, adminId, adminName);
  }

  static async getNomination(nominationId: string): Promise<Nomination | undefined> {
    let nom: Nomination | undefined;
    if (shouldUseGraphQL()) {
      nom = await GraphQLNominationService.getNomination(nominationId);
    } else {
      nom = nominationService.getNomination(nominationId);
    }
    if (nom) {
      return (await enrichWithResolvedPhotos([nom]))[0];
    }
    return undefined;
  }

  static async getMyNominations(nominatorId: string, nominatorEmail?: string): Promise<Nomination[]> {
    const startedAt = performance.now();
    console.log('[Perf][MyNominations] wrapper start getMyNominations', {
      nominatorId,
      nominatorEmail,
      useGraphQL: shouldUseGraphQL(),
    });
    let noms: Nomination[];
    if (shouldUseGraphQL()) {
      noms = await GraphQLNominationService.getMyNominations(nominatorId, nominatorEmail);
    } else {
      noms = nominationService.getMyNominations(nominatorId);
    }
    const enriched = await enrichWithResolvedPhotos(noms);
    console.log('[Perf][MyNominations] wrapper complete getMyNominations', {
      durationMs: Math.round(performance.now() - startedAt),
      count: enriched.length,
    });
    return enriched;
  }

  static async getReviewQueue(): Promise<Nomination[]> {
    const startedAt = performance.now();
    console.log('[Perf][ReviewQueue] wrapper start getReviewQueue', {
      useGraphQL: shouldUseGraphQL(),
    });
    let noms: Nomination[];
    if (shouldUseGraphQL()) {
      noms = await GraphQLNominationService.getReviewQueue();
    } else {
      noms = nominationService.getReviewQueue();
    }
    const enriched = await enrichWithResolvedPhotos(noms);
    console.log('[Perf][ReviewQueue] wrapper complete getReviewQueue', {
      durationMs: Math.round(performance.now() - startedAt),
      count: enriched.length,
    });
    return enriched;
  }

  static async getFeed(page: number = 1): Promise<PaginatedFeed> {
    let result: PaginatedFeed;
    if (shouldUseGraphQL()) {
      result = await GraphQLNominationService.getFeed(page);
    } else {
      result = nominationService.getFeed(page);
    }
    return { ...result, items: await enrichWithResolvedPhotos(result.items) };
  }

  static async getAllNominations(): Promise<Nomination[]> {
    const startedAt = performance.now();
    console.log('[Perf][ManageNominations] wrapper start getAllNominations', {
      useGraphQL: shouldUseGraphQL(),
    });
    let noms: Nomination[];
    if (shouldUseGraphQL()) {
      noms = await GraphQLNominationService.getAllNominations();
    } else {
      noms = nominationService.getAllNominations();
    }
    const enriched = await enrichWithResolvedPhotos(noms);
    console.log('[Perf][ManageNominations] wrapper complete getAllNominations', {
      durationMs: Math.round(performance.now() - startedAt),
      count: enriched.length,
    });
    return enriched;
  }

  static async getAllNominationsRaw(): Promise<Nomination[]> {
    const startedAt = performance.now();
    console.log('[Perf][ReviewQueue] wrapper start getAllNominationsRaw', {
      useGraphQL: shouldUseGraphQL(),
    });
    let noms: Nomination[];
    if (shouldUseGraphQL()) {
      noms = await GraphQLNominationService.getAllNominationsRaw();
    } else {
      noms = nominationService.getAllNominationsRaw();
    }
    const enriched = await enrichWithResolvedPhotos(noms);
    console.log('[Perf][ReviewQueue] wrapper complete getAllNominationsRaw', {
      durationMs: Math.round(performance.now() - startedAt),
      count: enriched.length,
    });
    return enriched;
  }

  static async addReviewerNote(
    input: ReviewerNoteInput,
    reviewerId: string,
    reviewerName: string
  ): Promise<{ success: boolean; note?: ReviewerNote; errors: string[] }> {
    if (shouldUseGraphQL()) {
      return GraphQLNominationService.addReviewerNote(input, reviewerId, reviewerName);
    }
    return nominationService.addReviewerNote(input, reviewerId, reviewerName);
  }

  static async getAuditEntries(nominationId?: string): Promise<AuditEntry[]> {
    if (shouldUseGraphQL() && nominationId) {
      return GraphQLNominationService.getAuditEntries(nominationId);
    }
    return nominationService.getAuditEntries();
  }

  static async createFeedComment(
    nominationId: string,
    authorId: string,
    authorName: string,
    content: string
  ): Promise<{ success: boolean; comment?: FeedComment; errors: string[] }> {
    if (shouldUseGraphQL()) {
      try {
        return await GraphQLNominationService.createFeedComment(nominationId, authorId, authorName, content);
      } catch {
        // GraphQL schema not deployed yet — fall back to local
      }
    }
    // Fallback: store comment in memory
    const comment: FeedComment = {
      id: `comment-${Date.now()}`,
      nominationId,
      authorId,
      authorName,
      content,
      createdAt: new Date().toISOString(),
    };
    const existing = localComments.get(nominationId) || [];
    existing.push(comment);
    localComments.set(nominationId, existing);
    return { success: true, comment, errors: [] };
  }

  static async getFeedComments(nominationId: string): Promise<FeedComment[]> {
    if (shouldUseGraphQL()) {
      try {
        return await GraphQLNominationService.getFeedComments(nominationId);
      } catch {
        // fall back to local
      }
    }
    return localComments.get(nominationId) || [];
  }

  static async deleteFeedComment(commentId: string): Promise<{ success: boolean; errors: string[] }> {
    if (shouldUseGraphQL()) {
      try {
        return await GraphQLNominationService.deleteFeedComment(commentId);
      } catch {
        // fall back to local
      }
    }
    // Remove from local store
    localComments.forEach((comments, key) => {
      localComments.set(key, comments.filter(c => c.id !== commentId));
    });
    return { success: true, errors: [] };
  }
}
